export declare namespace AppActivityDataDetailFormTokenSections {
	interface Root {
		padding: string;
		background: string;
		borderRadius: string;
	}

	interface Content {
		padding: string;
		blockGap: string;
		sectionsGap: string;
	}

	interface SectionHeader {
		minHeight: string;
		padding: string;
		fontSize: string;
		lineHeight: string;
		fontWeight: string;
		color: string;
		background: string;
	}

	interface DetailTitle {
		background: string;
		color: string;
		fontFamily: string;
		fontSize: string;
		fontWeight: string;
		lineHeight: string;
		padding: string;
		minHeight: string;
	}

	interface CapacityRow {
		borderColor: string;
		paddingY: string;
		gap: string;
		inputFieldWidth: string;
		inputFontSize: string;
		inputLineHeight: string;
		inputPaddingX: string;
		numberCell: {
			background: string;
			padding: string;
			borderRadius: string;
			fontSize: string;
			fontWeight: string;
			color: string;
			minHeight: string;
		};
		disabled: {
			numberCell: {
				background: string;
				color: string;
			};
			labelColor: string;
			inputBorderColor: string;
			inputTextColor: string;
			buttonIconColor: string;
		};
	}

	interface InfoRow {
		labelFontSize: string;
		labelLineHeight: string;
		subLabelFontSize: string;
		subLabelLineHeight: string;
		labelColor: string;
	}

	interface SelectionStateLabel {
		fontSize: string;
		lineHeight: string;
		fontWeight: string;
		color: string;
	}

	interface MarketCheckboxDisabledLabel {
		fontSize: string;
		lineHeight: string;
	}

	interface SaveButton {
		background: string;
		color: string;
	}

	interface OtherInfo {
		questionRow: {
			gap: string;
			paddingY: string;
			borderColor: string;
		};
		multiselect: {
			maxWidth: string;
		};
		readonlyLabel: {
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
			color: string;
		};
	}
}

export type AppActivityDataDetailFormTokens = {
	root: AppActivityDataDetailFormTokenSections.Root;
	content: AppActivityDataDetailFormTokenSections.Content;
	sectionHeader: AppActivityDataDetailFormTokenSections.SectionHeader;
	detailTitle: AppActivityDataDetailFormTokenSections.DetailTitle;
	capacityRow: AppActivityDataDetailFormTokenSections.CapacityRow;
	infoRow: AppActivityDataDetailFormTokenSections.InfoRow;
	selectionStateLabel: AppActivityDataDetailFormTokenSections.SelectionStateLabel;
	marketCheckboxesGap: string;
	marketCheckboxDisabledLabel: AppActivityDataDetailFormTokenSections.MarketCheckboxDisabledLabel;
	saveButton: AppActivityDataDetailFormTokenSections.SaveButton;
	otherInfo: AppActivityDataDetailFormTokenSections.OtherInfo;
};

export const root: AppActivityDataDetailFormTokenSections.Root = {
	padding: '0',
	background: '{content.background}',
	borderRadius: '{content.border.radius}',
};

export const content: AppActivityDataDetailFormTokenSections.Content = {
	padding: '24px',
	blockGap: '32px',
	sectionsGap: '24px',
};

export const sectionHeader: AppActivityDataDetailFormTokenSections.SectionHeader = {
	minHeight: '42px',
	padding: '8px',
	fontSize: '16px',
	lineHeight: '26px',
	fontWeight: '700',
	color: '{primary.600}',
	background: '{primary.50}',
};

export const detailTitle: AppActivityDataDetailFormTokenSections.DetailTitle = {
	background: '{surface.800}',
	color: '{primary.contrast.color}',
	fontFamily: 'inherit',
	fontSize: '20px',
	fontWeight: '500',
	lineHeight: '32px',
	/** Figma 11340: `py-8` + `px-16` → vertical 8px, horizontal 16px */
	padding: '8px 16px',
	minHeight: '48px',
};

export const capacityRow: AppActivityDataDetailFormTokenSections.CapacityRow = {
	borderColor: '{neutral.200}',
	paddingY: '12px',
	gap: '24px',
	inputFieldWidth: '11ch',
	inputFontSize: '18px',
	inputLineHeight: '30px',
	inputPaddingX: '2px',
	numberCell: {
		background: '{surface.50}',
		padding: '12px',
		borderRadius: '{border.radius.sm}',
		fontSize: '14px',
		fontWeight: '600',
		color: '{neutral.950}',
		minHeight: '32px',
	},
	/** Figma 11320-26877: disabled capacity eilutė – semantic token refs (theming.md). */
	disabled: {
		numberCell: {
			background: '{form.field.disabled.background}',
			color: '{form.field.disabled.color}',
		},
		labelColor: '{text.muted.color}',
		inputBorderColor: '{form.field.border.color}',
		inputTextColor: '{form.field.disabled.color}',
		buttonIconColor: '{form.field.border.color}',
	},
};

export const infoRow: AppActivityDataDetailFormTokenSections.InfoRow = {
	labelFontSize: '16px',
	labelLineHeight: '26px',
	subLabelFontSize: '14px',
	subLabelLineHeight: '24px',
	labelColor: '{neutral.950}',
};

export const selectionStateLabel: AppActivityDataDetailFormTokenSections.SelectionStateLabel = {
	fontSize: '14px',
	lineHeight: '24px',
	fontWeight: '400',
	color: '{surface.400}',
};

/** Figma 11320-26867: checkbox label – 16px, 26px. */
export const marketCheckboxDisabledLabel: AppActivityDataDetailFormTokenSections.MarketCheckboxDisabledLabel = {
	fontSize: '16px',
	lineHeight: '26px',
};

export const saveButton: AppActivityDataDetailFormTokenSections.SaveButton = {
	background: '{primary.color}',
	color: '{primary.contrast.color}',
};

export const otherInfo: AppActivityDataDetailFormTokenSections.OtherInfo = {
	questionRow: {
		gap: '24px',
		paddingY: '12px',
		borderColor: '{neutral.200}',
	},
	multiselect: {
		maxWidth: '411px',
	},
	readonlyLabel: {
		fontSize: '14px',
		lineHeight: '24px',
		fontWeight: '500',
		color: '{text.color}',
	},
};

/**
 * Veiklų duomenų žingsnio dešinės pusės (detail form) stiliai – pagal Figma (node 11340-26997, 11320-26855).
 * Info banner + ikona: Figma `11320:26857` 40×40, `11320:26858` info-circle instance 34.286px su Vector inset 12.5%
 * (matomas glyfas ~25.7px, ne 34px).
 * Detail title: Figma – flex 1 0 0, Blue.50 #F7FAFE, Subtitle 50 (Poppins 20px/500/32px).
 */
export default {
	root,
	content,
	sectionHeader,
	detailTitle,
	capacityRow,
	infoRow,
	selectionStateLabel,
	marketCheckboxesGap: '14px',
	marketCheckboxDisabledLabel,
	saveButton,
	otherInfo,
} satisfies AppActivityDataDetailFormTokens;
